/**
 * Thin HTTP client for /api/billing.
 *
 * Mirrors @papan/org-connections/client style. The server contract lives in
 * features/billing/api/routes.ts; the types here are the TRANSPORT shape, not
 * the DB shape — dates arrive as ISO strings, and money arrives in the unit
 * the server stores it in.
 *
 * ── Money units, because mixing them up bills the wrong amount ──────────
 *
 *  - `*MyrCents` — Malaysian sen. What the customer is actually charged;
 *    already converted server-side at invoice time. NEVER convert these again
 *    in the UI (see the note on `formatMoney` in the billing plan's
 *    budget-governance interface section).
 *  - `*UsdMicro` — USD microdollars. The metering unit for AI usage. Shown to
 *    explain how an overage charge was derived; not itself a charge.
 */
import { request } from '@papan/api-client'
import type {
  PlanAuthority, ManualBillingStatus, AmountDue,
  // Task 18: the org's own subscription-term history, shared with the
  // platform console's own `CycleUnit` (features/orgs/client/budget.ts).
  CycleUnit as SubscriptionCycleUnit,
} from '@papan/types'

export {
  promoAdminApi,
  type PromoGrantView,
  type PromoCodeView,
  type CreateGrantInput,
  type CreateCodeInput,
} from './promo-admin.js'

export {
  creditsAdminApi,
  adminAccountApi,
  type CreditAdjustInput,
  type CreditAdjustResult,
  type AdminTenantAccount,
} from './credits-admin.js'

export {
  webhookAdminApi,
  type StuckWebhookEventView,
} from './webhook-admin.js'

export {
  dunningAdminApi,
  type DunningRow,
  type DunningInvoice,
} from './dunning-admin.js'

export {
  planAdminApi,
  type PlanChangePath,
  type PlanPickerOption,
  type CustomerPlanState,
  type PlanChangeResult,
} from './plan-admin.js'

export {
  catalogAdminApi,
  type BillingSettingsView,
  type AdminPlanView,
  type AdminPackView,
  type PlanPatch,
  type PackPatch,
  type EntitlementsStale,
} from './catalog-admin.js'

/** Feature flags a plan grants beyond its first-class columns (mirrors
 *  BillingPlanFeatures server-side). Drives the derived feature bullets in the
 *  plan picker. */
export interface PlanFeatures {
  keybook?: boolean
}

export interface PlanView {
  id: string
  name: string
  audience: string
  /**
   * Lowercase ISO-4217 code every amount on this view is denominated in.
   *
   * The DEPLOYMENT's billing currency since Gate 1, not a property of the plan:
   * plan money lives in `billing_plan_prices(plan_id, currency)` and the server
   * only ever quotes the one currency this tenant is billed in. Optional so a
   * client built after a rolling deploy still parses an older payload; call
   * sites fall back rather than rendering an unlabeled number.
   */
  currency?: string
  /**
   * Recurring charge, MINOR units of `currency`. `0` = the free plan;
   * **`null` = the plan is not priced in this deployment's currency**, which is
   * NOT the same fact. A zero-priced card routes its button to CANCEL, so
   * conflating the two would offer a mispriced paid plan's subscriber a button
   * that ends their subscription. The plan LIST never contains these; the
   * tenant's own current plan can be one.
   */
  basePriceMinor: number | null
  /** Charge per seat beyond `includedSeats`, MINOR units. `null` = unpriced. */
  extraSeatPriceMinor: number | null
  /** Allowance included in `basePriceMinor`, billing-currency MICRO. */
  basePoolBillingMicro: number | null
  /** Allowance added per seat beyond `includedSeats`, billing-currency MICRO. */
  extraSeatAllowanceBillingMicro: number | null
  /** Seats covered by the base price before the per-seat terms apply. */
  includedSeats: number
  storageQuotaBytes: number | null
  /** Concurrent agent tasks this plan allows. `null` = the deployment default.
   *  Optional for the same rolling-deploy reason as `currency`: an older API
   *  instance omits it, and the card simply drops the bullet. */
  agentConcurrencyCap?: number | null
  features: PlanFeatures
  /** Catalog display order; lower first. */
  sortOrder: number
}

export interface SubscriptionView {
  id: string
  planId: string
  /** 'incomplete' | 'active' | 'past_due' | 'canceled' | … (Stripe's set). */
  status: string
  seatCount: number
  currentPeriodStart: string | null
  currentPeriodEnd: string | null
  cancelAtPeriodEnd: boolean
  /** Target of a downgrade scheduled for the period boundary, else null. The
   *  subscription keeps reporting the CURRENT plan until then, so this is the
   *  only signal that a pending change exists. */
  pendingPlanId: string | null
}

export interface UsageView {
  periodStart: string
  periodEnd: string
  /** Metered spend by funding bucket, microdollars. Covers BOTH ledgers (LLM
   *  `agent_usage` and non-LLM `resource_usage`) — the same numbers enforcement
   *  acts on. */
  freeBillingMicro: number
  extraBillingMicro: number
  graceBillingMicro: number
  /** Spend funded by promotional credit. Optional (rolling deploy). */
  promoBillingMicro?: number
  /** Spend drawn from the prepaid usage-credit balance. Distinct from the
   *  REMAINING balance on the credits endpoint — this is what was consumed. */
  prepaidBillingMicro?: number
  /** Every bucket summed. Optional (rolling deploy). */
  totalBillingMicro?: number
  /**
   * The plan's included allowance for this period, in billing-currency MICRO:
   * `base_pool + max(0, entitled − included) × extra_seat_allowance`, sized on
   * ENTITLED seats (what Stripe is charging) rather than live headcount — the
   * two drift and only the former is what enforcement grants.
   *
   * `null` when the plan carries no price in this deployment's currency. A
   * reporting route says "unknown"; an ASSIGNMENT refuses. Rendering `null` as
   * 0 would draw a full meter over an allowance nobody has measured.
   */
  includedBudgetBillingMicro: number | null
}

export interface SubscriptionResponse {
  plan: PlanView
  subscription: SubscriptionView | null
  seats: {
    /** Live headcount. */
    billable: number
    /** Seats the included allowance is sized from. Optional (rolling deploy). */
    entitled?: number
    /**
     * The enforced ceiling. THREE distinct states, and flattening any two of
     * them is the bug the seat cap is built around:
     *
     *   `undefined` — the API did not say (older instance, rolling deploy)
     *   `null`      — UNLIMITED. Render no denominator; never the word as a number.
     *   `0`         — a real ceiling meaning "nobody".
     *   `N`         — N billable seats.
     */
    max?: number | null
  }
  usage: UsageView
  /**
   * THIS TENANT's enforced limits — distinct from `plan`, which carries what
   * the plan DEFINES. The two diverge whenever an operator has customised the
   * tenant, or a plan row was edited without an explicit propagation run, so
   * rendering the plan's values on the customer's own page is the
   * "shown ≠ enforced" failure the console's tests exist to prevent.
   *
   * These are the columns the enforcers read: `assertStorageCapacity` for
   * storage, the agent-concurrency gate for the cap.
   *
   * OPTIONAL for a rolling deploy — an older API instance omits it, and every
   * consumer must treat absence as "unknown", never as zero.
   */
  tenant?: {
    storageQuotaBytes: number
    /** Already RESOLVED against the global default; never the raw nullable
     *  column, whose `null` means "inherit" rather than "no limit". */
    agentConcurrencyCap: number
    /** This tenant's RESOLVED included seats — the override when set, else the
     *  plan's default (A3). `null` = the plan carries no seat count. Rendered
     *  in preference to `plan.includedSeats`, which is only what the PRESET
     *  says and diverges once an operator sets a per-tenant override. */
    includedSeats?: number | null
  }
}

/**
 * The default card on file — display-safe fields only (brand/last4/expiry).
 * Never carries the PAN or CVC; edits happen in the Stripe-hosted portal.
 */
export interface PaymentMethodView {
  brand: string
  last4: string
  expMonth: number
  expYear: number
}

export interface InvoiceLine {
  type: string
  description: string
  quantity: number
  unitAmountMinor: number
  amountMinor: number
}

export interface InvoiceView {
  id: string
  periodStart: string
  periodEnd: string
  currency: string
  lines: InvoiceLine[]
  subtotalMinor: number
  taxMinor: number
  totalMinor: number
  status: string
  /** Stripe-hosted PDF/receipt. The authoritative document. */
  hostedInvoiceUrl: string | null
  dueAt: string | null
  paidAt: string | null
}

/** ───── Usage credits (0141/0144) ──────────────────────────────────
 *
 * The prepaid balance a tenant spends once its included budget is gone. With
 * overage dropped this is the ONLY way past the included allowance, which is
 * why the card is on this page rather than tucked behind a link.
 *
 * Two units meet here and must not be confused: a pack is PRICED in the
 * deployment's charged currency, in MINOR units (`priceMinor` — what the
 * card is charged) and GRANTS USD microdollars (`creditBillingMicro` — what gets
 * metered away). The ratio is frozen per pack at sync time, so the button can
 * state both truthfully.
 */
export interface CreditPackView {
  id: string
  name: string
  /** The pack's own ISO-4217 code. Optional for rolling-deploy tolerance;
   *  absent ⇒ 'myr', which is what every pre-0147 row was. */
  currency?: string
  priceMinor: number
  creditBillingMicro: number
  /** False when the pack has no Stripe price yet (`pnpm billing:sync-credit-packs`
   *  never ran). Render it disabled with a reason rather than hiding it — a
   *  missing pack looks like a catalog change, a disabled one looks like the
   *  deployment gap it is. */
  purchasable: boolean
}

export interface CreditTransactionView {
  id: string
  /** 'topup' | 'consume' | 'adjust' | 'refund'. */
  type: string
  /** SIGNED BILLING micro-units: positive in, negative out. */
  amountBillingMicro: number
  balanceAfterBillingMicro: number
  /** The unit this ROW moved. Per row, because a ledger outlives a
   *  deployment's currency; equal to the top-level `currency` on every row a
   *  live system can produce. No rate is carried — a top-up of RM50 IS RM50. */
  billingCurrency: string
  createdAt: string
}

export interface CreditsResponse {
  balanceBillingMicro: number
  /** The admin hard stop. False means "stop spending my money". */
  enabled: boolean
  /** The threshold an admin explicitly set, or null. */
  lowBalanceThresholdBillingMicro: number | null
  /**
   * The threshold actually IN FORCE — an explicit one, else the derived
   * default. Render this, not the field above: the stored column is null for
   * almost every tenant, and an empty input reads as "no warning is armed"
   * when in fact one is.
   */
  effectiveLowBalanceThresholdBillingMicro: number | null
  /** Which of the three states the effective threshold is in. `none` means
   *  nothing will warn — no purchase exists to size a default against. */
  lowBalanceThresholdSource: 'explicit' | 'default_last_topup' | 'none'
  /** The default's fraction of the last top-up (0.2), for the hint copy. */
  lowBalanceDefaultFraction: number
  viewerCanManageBilling: boolean
  /**
   * The deployment's charged currency — what `bounds` is denominated in, and
   * what a CUSTOM amount is charged in. A pack's own `currency` wins for that
   * pack's price. Optional for rolling-deploy tolerance; absent ⇒ 'myr'.
   */
  currency?: string
  /** Server-authoritative purchase bounds, in `currency`'s minor units.
   *  Mirrored client-side for UX only — the route re-validates, and it is the
   *  only thing between a typo and a real card charge. */
  bounds: { floorMinor: number; ceilingMinor: number }
  packs: CreditPackView[]
  transactions: CreditTransactionView[]
}

/**
 * The subset of Settings → Storage's composition payload the Billing key-info
 * card needs. The full `StorageCompositionResponse` lives in `@papan/orgs` and
 * carries per-category and duplicate detail this card does not; only the quota
 * and the four size buckets are read here.
 */
export interface StorageCompositionView {
  quotaBytes: number
  live: { bytes: number }
  reclaimable: {
    bin: { bytes: number }
    versions: { bytes: number }
    quarantine: { bytes: number }
  }
}

/** Either a catalog pack or a custom MYR amount — never both. */
export type CreditPurchase =
  | { packId: string }
  | { customAmountMinor: number }

/** `null` paid-through = untracked = NOT overdue. */
export interface ManualStatementResponse {
  applicable: boolean
  paidThrough?: string | null
  status?: ManualBillingStatus
  daysOverdue?: number | null
  note?: string | null
  /** Absent when the caller may not see money — see `getManualStatement`.
   *  Shape mirrors `AmountDue` (@papan/types) plus the live `memberCount` the
   *  figure was multiplied by, so a reader can check the arithmetic. */
  amountDue?: AmountDue & { memberCount: number }
}

/**
 * One row of the org's OWN subscription-term history (`GET
 * /api/billing/subscription-terms`). Mirrors the platform console's
 * `SubscriptionTerm` (`features/orgs/client/budget.ts`, Task 16) minus
 * `tenantId` — this is always the caller's own tenant, so echoing the id back
 * would be a value with no second reading.
 */
export interface SubscriptionTermView {
  id: string
  planId: string
  /** Display name resolved server-side from the full plan catalog (presets
   *  included), so a manually-assigned preset never renders as its raw id.
   *  `null` when the plan row no longer exists. */
  planName: string | null
  /** Midnight UTC of the day it takes effect, as an ISO string. */
  effectiveFrom: string
  /** `null` until this term has taken effect. */
  appliedAt: string | null
  cycleCount: number
  cycleUnit: SubscriptionCycleUnit
  note: string | null
  createdBy: string | null
  createdAt: string
  /** `current` = what `resolveCurrentTerm` picks right now; `future` = not
   *  yet effective; `past` = superseded or historical. */
  status: 'past' | 'current' | 'future'
}

export const billingApi = {
  /**
   * The active catalog. Always live — even a free tenant sees upgrade options.
   *
   * `billingEnabled` reports whether the WRITE routes exist on this install;
   * they 404 when the flag is off, so the UI hides its actions rather than
   * offering a button that fails.
   *
   * `planAuthority` reports WHO DECIDES plans. Under `'manual'` an operator
   * assigns them and invoices out of band, so there is nothing to pick and no
   * upgrade to sell — both consumers (`BillingPage`, `StorageUsagePage`) hide
   * their offer rather than disable it, because a disabled control still
   * advertises something that is not for sale. Already RESOLVED server-side,
   * so `'provider'` covers the never-configured deployment; do not re-derive.
   */
  listPlans: () => request<{
    plans: PlanView[]
    billingEnabled: boolean
    planAuthority: PlanAuthority
  }>('/api/billing/plans'),

  /** Effective plan + seats + this period's usage. Any member may read it. */
  getSubscription: () => request<SubscriptionResponse>('/api/billing/subscription'),

  /**
   * Storage USED against the tenant's quota, for the Billing page's key-info
   * card. The subscription payload carries the quota but deliberately not the
   * used bytes (that needs a live drive walk); this is the same read Settings →
   * Storage makes. Called against `/api/orgs/:id/storage` directly because that
   * route (and its client) live in `@papan/orgs`, which `features/billing`
   * cannot import — the URL is a string, not a code dependency, so no layering
   * is crossed. Membership-gated only. `usedBytes` is summed the same way the
   * storage page and the quota enforcer sum it: live + bin + versions +
   * quarantine.
   */
  getStorageComposition: (tenantId: string) =>
    request<StorageCompositionView>(`/api/orgs/${tenantId}/storage`),

  /**
   * What a manually-billed customer owes, and until when.
   *
   * `applicable: false` on a provider-billed deployment — asked
   * unconditionally, because a 404 would be indistinguishable from the route
   * being absent mid-rolling-deploy.
   *
   * `amountDue` is OMITTED for a member without `manage_billing:read`, never
   * zeroed (decision D2): an absent field cannot be misread as "you owe
   * nothing". The paid-through date goes to everyone, because it explains a
   * write freeze they can already see.
   */
  getManualStatement: () => request<ManualStatementResponse>('/api/billing/manual-statement'),

  /**
   * The org's OWN manual-subscription-term history, newest first.
   * `manage_billing:read`, same gate as `getSubscription`. Takes no tenant id
   * — the server derives it from the session, so a caller cannot ask about
   * another organization's terms.
   */
  listSubscriptionTerms: () => request<{ terms: SubscriptionTermView[] }>('/api/billing/subscription-terms'),

  /** Billing history from the local mirror. Owner/admin only (403 otherwise). */
  listInvoices: () => request<{ invoices: InvoiceView[] }>('/api/billing/invoices'),

  /**
   * The default card on file, read live from Stripe. Owner/admin only (403
   * otherwise); `paymentMethod` is null when there's no customer or no card.
   */
  getPaymentMethod: () =>
    request<{ paymentMethod: PaymentMethodView | null }>('/api/billing/payment-method'),

  /**
   * Redeem a promo code onto a tenant (promo plan P4).
   *
   * `tenantId` is passed explicitly rather than riding the ambient
   * `X-Tenant-Id`: the redeem page lets the caller CHOOSE which of their orgs
   * receives the grant, which is usually not the one they are currently
   * viewing. Attaching a promo to the wrong tenant is not undoable by the
   * tenant — only a platform admin can revoke a grant.
   *
   * Rejections are deliberately uninformative (one shape for unknown, revoked,
   * expired and exhausted) — the endpoint must not become a code-validity
   * oracle. `reason` distinguishes only the states the caller already knows
   * about: `already_redeemed`, `rate_limited`, `missing_code`.
   */
  redeemPromoCode: (code: string, tenantId: string) =>
    request<{ grant: { id: string; tenantId: string; effects: unknown[]; startsAt: string; endsAt: string } }>(
      '/api/billing/promo/redeem',
      { method: 'POST', body: JSON.stringify({ code }), headers: { 'x-tenant-id': tenantId } },
    ),

  /** Start Checkout for a paid plan. Returns a Stripe-hosted URL to redirect to. */
  subscribe: (planId: string) =>
    request<{ url: string }>('/api/billing/subscribe', {
      method: 'POST',
      body: JSON.stringify({ planId }),
    }),

  /**
   * Switch plans. Upgrades apply immediately with prorations; downgrades are
   * deferred to period end, so `effective` tells the UI which message to show.
   */
  changePlan: (planId: string) =>
    request<{ ok: true; effective: 'immediate' | 'period_end' }>('/api/billing/change-plan', {
      method: 'POST',
      body: JSON.stringify({ planId }),
    }),

  /** Cancel at period end — the customer keeps what they paid for. */
  cancel: () =>
    request<{ ok: true; effective: 'period_end' }>('/api/billing/cancel', { method: 'POST' }),

  /**
   * A Stripe-hosted Billing Portal session for payment methods and receipts.
   * Card data must never transit Papan, so this is a redirect, not a form.
   */
  openPortal: () => request<{ url: string }>('/api/billing/portal', { method: 'POST' }),

  /**
   * Balance, settings, purchasable packs and ledger history.
   *
   * Readable by EVERY member, unlike /invoices — this powers the "your org is
   * out of AI credit" story for exactly the people who hit it. `limit`/`before`
   * page the history (keyset on createdAt, server caps at 200).
   */
  getCredits: (opts: { limit?: number; before?: string } = {}) => {
    const qs = new URLSearchParams()
    if (opts.limit != null) qs.set('limit', String(opts.limit))
    if (opts.before) qs.set('before', opts.before)
    const q = qs.toString()
    return request<CreditsResponse>(`/api/billing/credits${q ? `?${q}` : ''}`)
  },

  /**
   * Start a one-off Checkout Session for credits. Returns a Stripe-hosted URL.
   *
   * The balance moves on the WEBHOOK, never on the return trip — a customer
   * who lands back here has paid, but may briefly still see the old figure.
   */
  buyCredits: (purchase: CreditPurchase) =>
    request<{ url: string }>('/api/billing/credits/checkout', {
      method: 'POST',
      body: JSON.stringify(purchase),
    }),

  /**
   * Enable/disable credits and set the low-balance threshold. Owner/admin only.
   *
   * An omitted field is LEFT ALONE server-side, so toggling `enabled` cannot
   * silently erase the threshold. Send `lowBalanceThresholdBillingMicro: null` to
   * clear it deliberately.
   */
  updateCreditSettings: (patch: {
    enabled?: boolean
    lowBalanceThresholdBillingMicro?: number | null
  }) =>
    request<{ enabled: boolean; lowBalanceThresholdBillingMicro: number | null; balanceBillingMicro: number }>(
      '/api/billing/credits/settings',
      { method: 'PUT', body: JSON.stringify(patch) },
    ),
}
